import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { confirm } from '@/components/dialog'
import Icon from '@/components/icon'
import { colors, fontSizes, fontWeights } from '@/styles'
import { Button } from '@/components/button'
import formatAddendumPreview from '@/utils/format-addendum-preview'
import EditAddendum from './addendum-modal/edit-addendum'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 80px;
`

const ContainerWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px 0;
`

const ListWrapper = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${colors.white};
  padding: 24px;
  cursor: pointer;
  border: 1px solid ${colors.grey88};
  border-radius: 8px;
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06);

  :hover {
    opacity: 0.8;
  }

  ${props =>
    props.header &&
    `
    background-color: ${colors.lightGrey};
    min-height: 62px;
  `}
`

const EditText = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  line-height: 22px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EditIcon = styled(Icon)`
  g {
    stroke: ${colors.regular};
  }
`

const TextWrapper = styled.div`
  min-height: 44px;
  max-width: 93%;
`

const TitleText = styled(EditText)`
  color: ${colors.black};
`

const Description = styled.p`
  color: ${colors.grey56};
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`

const AddendumList = ({
  contractAddendums,
  handleShowAddendumsModal,
  removeContractAddendum,
  updateContractAddendum,
  variables
}) => {
  const { t } = useTranslation()

  const handleRemoveContractAddendum = addendum => {
    return confirm(
      t('l.contract.edit.confirm_remove_addendum.title', 'Remove Addendum'),
      t(
        'l.contract.edit.confirm_remove_addendum.text',
        'Are you sure you want to remove this addendum from the Tenancy Agreement?'
      ),
      {
        okLabel: t('common.remove', 'Remove'),
        cancelLabel: t('common.cancel', 'Cancel'),
        isWarning: true
      }
    ).then(async () => {
      removeContractAddendum(addendum)
    })
  }

  return (
    <Wrapper>
      <ContainerWrapper>
        <ListWrapper>
          {contractAddendums.map((addendum, index) => {
            const editAddendum = {
              component: EditAddendum,
              addendumType: 'one_time',
              isEditing: true,
              addendum,
              titleText: t('l.contract.edit.edit_addendum.title', 'Edit Addendum'),
              footnote: t(
                'l.contract.edit.edit_addendum_update_added_info.text',
                'Please note: The changes you make here apply only to this listing/contract and will not change anything else.'
              ),
              buttonText: t('l.contract.edit.save_changes.button', 'Save Changes'),
              onSubmit: updatedAddendum => updateContractAddendum(updatedAddendum, addendum),
              onSubmitComplete: 'close',
              onDelete: () => handleRemoveContractAddendum(addendum),
              onDeleteComplete: 'close'
            }

            return (
              <ItemWrapper
                key={`${addendum.title}-${index}`}
                onClick={() => handleShowAddendumsModal(editAddendum)}
              >
                <TextWrapper>
                  <TitleText>{addendum.title}</TitleText>
                  <Description>{formatAddendumPreview(addendum, t, variables)}</Description>
                </TextWrapper>
                <EditIcon id="edit" width="20" height="20" />
              </ItemWrapper>
            )
          })}
        </ListWrapper>
        <Button theme="secondary" onClick={() => handleShowAddendumsModal()}>
          <Icon id="cross_plus" width="14" height="14" />
          {t('l.contract.edit.add_addendums.button', 'Add Addendums')}
        </Button>
      </ContainerWrapper>
    </Wrapper>
  )
}

export default AddendumList
